import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { 
  clearFilters, 
  addPersonFilter, 
  removePersonFilter, 
  addPlaceFilter, 
  removePlaceFilter,
  addYearFilter,
  removeYearFilter,
  addEventFilter,
  removeEventFilter
} from '../../../slice/filterSlice';
import { tagService } from '../../../services';
import { Tag } from '../../../types';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('DockContent');

/**
 * DockContent - Shared filter UI logic for both GlassDock and FooterBar
 * Displays: All/Clear button + top 4 tags with icons
 * No positioning or layout - just the content
 */
export default function DockContent() {
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const [topTags, setTopTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);

   // Load top 4 tags on mount
   useEffect(() => {
     const loadTopTags = async () => {
       try {
         setLoading(true);
         const tags = await tagService.getTopTags(4);
         // Defensive guard: ensure tags is an array before setting state
         // (handles edge cases where malformed/error responses slip through)
         setTopTags(Array.isArray(tags) ? tags : []);
       } catch (err) {
         logger.error('Error loading top tags:', err);
       } finally {
         setLoading(false);
       }
     };
     loadTopTags();
   }, []);

  // Check if any filters are active
  const hasActiveFilters = 
    filters.people.length > 0 || 
    filters.places.length > 0 || 
    filters.years.length > 0 || 
    filters.events.length > 0 || 
    filters.visibility !== 'all';

  // Check if a tag is selected
  const isTagSelected = (tag: Tag): boolean => {
    switch (tag.type) {
      case 'person':
        return filters.people.includes(tag.name);
      case 'place':
        return filters.places.includes(tag.name);
      case 'year':
        return filters.years.includes(tag.name);
      case 'event':
        return filters.events.includes(tag.name);
      default:
        return false;
    }
  };

  // Toggle tag selection
  const handleTagClick = (tag: Tag) => {
    const isSelected = isTagSelected(tag);
    switch (tag.type) {
      case 'person':
        if (isSelected) {
          dispatch(removePersonFilter(tag.name));
        } else {
          dispatch(addPersonFilter(tag.name));
        }
        break;
      case 'place':
        if (isSelected) {
          dispatch(removePlaceFilter(tag.name));
        } else {
          dispatch(addPlaceFilter(tag.name));
        }
        break;
      case 'year':
        if (isSelected) {
          dispatch(removeYearFilter(tag.name));
        } else {
          dispatch(addYearFilter(tag.name));
        }
        break;
      case 'event':
        if (isSelected) {
          dispatch(removeEventFilter(tag.name));
        } else {
          dispatch(addEventFilter(tag.name));
        }
        break;
    }
  };

  // Handle "All/Clear" button
  const handleAllClear = () => {
    if (hasActiveFilters) {
      dispatch(clearFilters());
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '0.5rem 1rem', color: 'var(--text-secondary)' }}>
        Loading...
      </div>
    );
  }

  return (
    <>
      {/* All/Clear Button */}
      <button
        className={`dock-filter ${!hasActiveFilters ? 'active' : ''}`}
        onClick={handleAllClear}
        title={hasActiveFilters ? 'Clear all filters' : 'All photos'}
      >
        {hasActiveFilters ? 'Clear' : 'All'}
      </button>

      {/* Separator */}
      <div className="dock-separator" />

      {/* Top Tags */}
      {topTags.map((tag) => (
        <button
          key={tag.id}
          className={`dock-filter ${isTagSelected(tag) ? 'active' : ''}`}
          onClick={() => handleTagClick(tag)}
          title={`Filter by ${tag.name}`}
        >
          <span style={{ marginRight: '0.5rem' }}>
            {tag.type === 'person' && '👤'}
            {tag.type === 'place' && '📍'}
            {tag.type === 'year' && '📅'}
            {tag.type === 'event' && '🎉'}
            {tag.type === 'custom' && '🏷️'}
          </span>
          {tag.name}
        </button>
      ))}
    </>
  );
}
